import React, { useEffect, useState } from 'react';
import {
  Mail,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Send,
  Code2
} from 'lucide-react';
import { emailNotificacaoService, ConfiguracaoEmail, NotificacaoEmailTemplate, NotificacaoEmailLog } from '../../services/emailNotificacaoService';

const STATUS_LABEL: Record<string, { texto: string; cor: string }> = {
  enviando: { texto: 'Enviando', cor: 'bg-slate-100 text-slate-600' },
  enviado: { texto: 'Enviado', cor: 'bg-blue-100 text-blue-700' },
  entregue: { texto: 'Entregue', cor: 'bg-emerald-100 text-emerald-700' },
  erro: { texto: 'Erro', cor: 'bg-red-100 text-red-700' },
  bloqueado_sem_consentimento: { texto: 'Sem consentimento do cliente', cor: 'bg-amber-100 text-amber-700' },
};

export function ConfiguracaoEmailSection() {
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'sucesso' | 'erro'; mensagem: string } | null>(null);
  const [guiaAberto, setGuiaAberto] = useState(false);

  const [config, setConfig] = useState<ConfiguracaoEmail | null>(null);
  const [ativo, setAtivo] = useState(false);
  const [remetenteNome, setRemetenteNome] = useState('');
  const [remetenteEmail, setRemetenteEmail] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPorta, setSmtpPorta] = useState(587);
  const [smtpSeguranca, setSmtpSeguranca] = useState<'ssl' | 'starttls' | 'nenhuma'>('starttls');
  const [smtpUsuario, setSmtpUsuario] = useState('');
  const [smtpSenha, setSmtpSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [emailTeste, setEmailTeste] = useState('');

  const [templates, setTemplates] = useState<NotificacaoEmailTemplate[]>([]);
  const [templateEmEdicao, setTemplateEmEdicao] = useState<Record<string, { assunto: string; corpo_html: string; ativo: boolean }>>({});
  const [salvandoTemplate, setSalvandoTemplate] = useState<string | null>(null);
  const [eventoExpandido, setEventoExpandido] = useState<string | null>(null);

  const [logs, setLogs] = useState<NotificacaoEmailLog[]>([]);
  const [reenviandoId, setReenviandoId] = useState<number | null>(null);

  const showFeedback = (tipo: 'sucesso' | 'erro', mensagem: string) => {
    setFeedback({ tipo, mensagem });
    setTimeout(() => setFeedback(null), 5000);
  };

  const carregarTudo = async () => {
    setLoading(true);
    try {
      const [cfg, tpls, lgs] = await Promise.all([
        emailNotificacaoService.getConfiguracao(),
        emailNotificacaoService.getTemplates(),
        emailNotificacaoService.getLogs(30),
      ]);
      setConfig(cfg);
      setAtivo(cfg.ativo);
      setRemetenteNome(cfg.remetente_nome || '');
      setRemetenteEmail(cfg.remetente_email || '');
      setSmtpHost(cfg.smtp_host || '');
      setSmtpPorta(cfg.smtp_porta || 587);
      setSmtpSeguranca(cfg.smtp_seguranca || 'starttls');
      setSmtpUsuario(cfg.smtp_usuario || '');
      setTemplates(tpls);
      const inicial: Record<string, { assunto: string; corpo_html: string; ativo: boolean }> = {};
      tpls.forEach((t) => {
        inicial[t.evento_codigo] = { assunto: t.assunto || '', corpo_html: t.corpo_html || '', ativo: t.ativo };
      });
      setTemplateEmEdicao(inicial);
      setLogs(lgs);
      if (!cfg.smtp_host) setGuiaAberto(true);
    } catch (e: any) {
      showFeedback('erro', e.message || 'Falha ao carregar configuração de e-mail.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarTudo();
  }, []);

  const handleSalvarConfiguracao = async () => {
    setSalvando(true);
    try {
      await emailNotificacaoService.salvarConfiguracao({
        ativo,
        remetente_nome: remetenteNome.trim(),
        remetente_email: remetenteEmail.trim(),
        smtp_host: smtpHost.trim(),
        smtp_porta: smtpPorta,
        smtp_seguranca: smtpSeguranca,
        smtp_usuario: smtpUsuario.trim(),
      });

      if (smtpSenha.trim()) {
        await emailNotificacaoService.salvarSenhaSmtp(smtpSenha.trim());
        setSmtpSenha('');
      }

      showFeedback('sucesso', 'Configuração de e-mail salva com sucesso!');
      await carregarTudo();
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao salvar configuração.');
    } finally {
      setSalvando(false);
    }
  };

  const handleTestarConexao = async () => {
    setTestando(true);
    try {
      const res = await emailNotificacaoService.testarConexao(emailTeste.trim() || undefined);
      showFeedback(res.sucesso ? 'sucesso' : 'erro', res.mensagem);
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao testar conexão.');
    } finally {
      setTestando(false);
    }
  };

  const handleSalvarTemplate = async (evento_codigo: string) => {
    const dados = templateEmEdicao[evento_codigo];
    if (!dados) return;
    setSalvandoTemplate(evento_codigo);
    try {
      await emailNotificacaoService.salvarTemplate(evento_codigo, dados);
      showFeedback('sucesso', 'Modelo de e-mail salvo.');
      await carregarTudo();
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao salvar modelo.');
    } finally {
      setSalvandoTemplate(null);
    }
  };

  const handleReenviar = async (logId: number) => {
    setReenviandoId(logId);
    try {
      const res = await emailNotificacaoService.reenviarNotificacao(logId);
      showFeedback(res.sucesso ? 'sucesso' : 'erro', res.mensagem);
      await carregarTudo();
    } catch (e: any) {
      showFeedback('erro', e.message || 'Erro ao reenviar notificação.');
    } finally {
      setReenviandoId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {feedback && (
        <div className={`p-3.5 rounded-xl border flex items-start gap-3 text-sm font-medium ${
          feedback.tipo === 'sucesso' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {feedback.tipo === 'sucesso' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
          <span>{feedback.mensagem}</span>
        </div>
      )}

      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm border border-slate-700/50">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center">
            <Mail className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-black">Notificações por E-mail (SMTP)</h2>
            <p className="text-slate-400 text-xs mt-0.5">Mesmos eventos do WhatsApp (OS confirmada, em rota, aguardando assinatura, finalizada, cancelada, fatura disponível) — só para quem deu consentimento.</p>
          </div>
        </div>
        <div className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shrink-0 ${
          ativo && config?.possui_senha_smtp ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-400/30' : 'bg-slate-700/50 text-slate-400 border border-slate-600/50'
        }`}>
          {ativo && config?.possui_senha_smtp ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {ativo && config?.possui_senha_smtp ? 'Ativo' : 'Inativo'}
        </div>
      </div>

      {/* Guia de configuração SMTP */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <button
          type="button"
          onClick={() => setGuiaAberto((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 cursor-pointer"
        >
          <span className="text-sm font-bold text-slate-800">Como configurar o SMTP (passo a passo)</span>
          {guiaAberto ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {guiaAberto && (
          <div className="px-5 pb-5 space-y-4 text-sm text-slate-600 border-t border-slate-100 pt-4">
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                Você precisa de uma conta de e-mail com acesso SMTP liberado. Pode ser: (a) o mesmo SMTP customizado que você já tenha configurado em <strong>Supabase → Authentication → Emails → SMTP Settings</strong> (se já usa um, copie de lá host/porta/usuário/senha); (b) um provedor de e-mail transacional (Resend, SendGrid, Amazon SES, Mailgun — todos oferecem um "SMTP Relay" com host/porta/usuário/senha próprios); ou (c) uma caixa de e-mail comum (Gmail, Outlook, e-mail da própria empresa).
              </li>
              <li>
                Preencha <strong>Host SMTP</strong> e <strong>Porta</strong>. Exemplos comuns:
                <div className="mt-2 grid gap-1.5 text-xs">
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100"><code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">smtp.gmail.com</code> — porta 587 (STARTTLS), exige "Senha de app" do Google, não a senha normal.</div>
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100"><code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">smtp.office365.com</code> — porta 587 (STARTTLS).</div>
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100"><code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">smtp.resend.com</code> — porta 465 (SSL) ou 587 (STARTTLS), usuário <code className="bg-white px-1 rounded">resend</code> e senha = sua API Key do Resend.</div>
                </div>
              </li>
              <li>
                Escolha o tipo de <strong>Segurança</strong>: SSL/TLS implícito costuma ser a porta 465; STARTTLS (o mais comum) costuma ser a porta 587; "Nenhuma" (porta 25) só deve ser usada em relays internos confiáveis, nunca pela internet aberta.
              </li>
              <li>
                Preencha <strong>Usuário</strong> e <strong>Senha</strong> SMTP (a senha é salva de forma criptografada no Vault do Supabase, nunca em texto puro).
              </li>
              <li>
                Preencha "Nome do remetente" (ex.: Voope Transportes) e "E-mail do remetente" — o mesmo endereço (ou um autorizado pelo provedor/domínio) usado para autenticar no SMTP, senão o provedor pode rejeitar ou marcar como spam.
              </li>
              <li>
                Ao contrário do WhatsApp, o e-mail <strong>não precisa de aprovação de template</strong> — os modelos abaixo (assunto + corpo em HTML) já vêm preenchidos com um texto padrão para cada evento e podem ser enviados assim que você marcar "Ativo" em cada um. A logo, o cabeçalho e o rodapé (com os dados da empresa) são aplicados automaticamente em cima do que você editar aqui — usando a mesma logo cadastrada em Configurações → Dados da Empresa.
              </li>
              <li>
                A mesma regra do WhatsApp vale aqui: só é enviado e-mail para clientes com o campo <strong>"Aceita notificações por e-mail"</strong> marcado no cadastro (Pessoas → editar).
              </li>
              <li>
                Marque "Notificações ativas", salve e use "Enviar E-mail de Teste" para confirmar que a conexão SMTP está correta.
              </li>
            </ol>
          </div>
        )}
      </div>

      {/* Conexão SMTP */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Conexão SMTP</h3>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} className="w-4 h-4 accent-blue-600" />
            Notificações ativas
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2 grid gap-4 grid-cols-3">
            <div className="col-span-2">
              <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Host SMTP</label>
              <input
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="smtp.suaempresa.com.br"
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Porta</label>
              <input
                type="number"
                value={smtpPorta}
                onChange={(e) => setSmtpPorta(Number(e.target.value) || 587)}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Segurança</label>
            <select
              value={smtpSeguranca}
              onChange={(e) => setSmtpSeguranca(e.target.value as 'ssl' | 'starttls' | 'nenhuma')}
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            >
              <option value="starttls">STARTTLS (porta 587, mais comum)</option>
              <option value="ssl">SSL/TLS implícito (porta 465)</option>
              <option value="nenhuma">Nenhuma (porta 25 — não recomendado)</option>
            </select>
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Usuário SMTP</label>
            <input
              value={smtpUsuario}
              onChange={(e) => setSmtpUsuario(e.target.value)}
              placeholder="usuario@suaempresa.com.br"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">
              Senha SMTP {config?.possui_senha_smtp && <span className="text-emerald-600 normal-case font-semibold">(configurada — deixe em branco para manter)</span>}
            </label>
            <div className="relative">
              <input
                type={mostrarSenha ? 'text' : 'password'}
                value={smtpSenha}
                onChange={(e) => setSmtpSenha(e.target.value)}
                placeholder={config?.possui_senha_smtp ? '••••••••••••••••••••' : 'Senha ou token SMTP'}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 pr-10 font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
              <button type="button" onClick={() => setMostrarSenha((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer">
                {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Nome do remetente</label>
            <input
              value={remetenteNome}
              onChange={(e) => setRemetenteNome(e.target.value)}
              placeholder="Voope Transportes"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">E-mail do remetente</label>
            <input
              value={remetenteEmail}
              onChange={(e) => setRemetenteEmail(e.target.value)}
              placeholder="notificacoes@suaempresa.com.br"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={handleSalvarConfiguracao}
            disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-900/20 transition-all disabled:opacity-50 cursor-pointer"
          >
            {salvando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {salvando ? 'Salvando...' : 'Salvar Configuração'}
          </button>
          <div className="flex items-center gap-2">
            <input
              value={emailTeste}
              onChange={(e) => setEmailTeste(e.target.value)}
              placeholder="e-mail de destino do teste (opcional)"
              className="text-xs border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            <button
              type="button"
              onClick={handleTestarConexao}
              disabled={testando}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {testando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {testando ? 'Enviando...' : 'Enviar E-mail de Teste'}
            </button>
          </div>
        </div>
      </div>

      {/* Eventos e Modelos de E-mail */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-800">Eventos e Modelos de E-mail</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Os modelos já vêm com um texto padrão. O interruptor "Ativo" liga/desliga o envio desse evento — mesmo ativo, só é enviado a clientes com consentimento marcado no cadastro (Pessoas).
          </p>
        </div>
        <div className="divide-y divide-slate-100">
          {templates.map((t) => {
            const edit = templateEmEdicao[t.evento_codigo] || { assunto: '', corpo_html: '', ativo: false };
            const expandido = eventoExpandido === t.evento_codigo;
            return (
              <div key={t.evento_codigo} className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{t.descricao}</p>
                    {t.variaveis_ajuda && <p className="text-3xs text-slate-400 mt-0.5">{t.variaveis_ajuda}</p>}
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-3xs font-bold shrink-0 ${
                    edit.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {edit.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>

                <div>
                  <label className="block text-3xs font-bold text-slate-500 uppercase tracking-wide mb-1">Assunto</label>
                  <input
                    value={edit.assunto}
                    onChange={(e) => setTemplateEmEdicao((s) => ({ ...s, [t.evento_codigo]: { ...edit, assunto: e.target.value } }))}
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => setEventoExpandido(expandido ? null : t.evento_codigo)}
                    className="text-xs font-bold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 cursor-pointer"
                  >
                    <Code2 className="w-3.5 h-3.5" />
                    {expandido ? 'Ocultar corpo do e-mail (HTML)' : 'Editar corpo do e-mail (HTML)'}
                  </button>
                  {expandido && (
                    <textarea
                      value={edit.corpo_html}
                      onChange={(e) => setTemplateEmEdicao((s) => ({ ...s, [t.evento_codigo]: { ...edit, corpo_html: e.target.value } }))}
                      rows={6}
                      className="mt-2 w-full text-xs font-mono border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    />
                  )}
                </div>

                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={edit.ativo}
                      onChange={(e) => setTemplateEmEdicao((s) => ({ ...s, [t.evento_codigo]: { ...edit, ativo: e.target.checked } }))}
                      className="w-4 h-4 accent-blue-600"
                    />
                    Ativo
                  </label>
                  <button
                    type="button"
                    onClick={() => handleSalvarTemplate(t.evento_codigo)}
                    disabled={salvandoTemplate === t.evento_codigo}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {salvandoTemplate === t.evento_codigo ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Histórico de Notificações */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Histórico de E-mails Enviados</h3>
          <button type="button" onClick={carregarTudo} className="text-slate-400 hover:text-slate-600 cursor-pointer" title="Atualizar">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {logs.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">Nenhum e-mail de notificação enviado ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-3xs">
                <tr>
                  <th className="text-left px-4 py-2.5 font-bold">Data</th>
                  <th className="text-left px-4 py-2.5 font-bold">Evento</th>
                  <th className="text-left px-4 py-2.5 font-bold">OS</th>
                  <th className="text-left px-4 py-2.5 font-bold">E-mail</th>
                  <th className="text-left px-4 py-2.5 font-bold">Status</th>
                  <th className="text-left px-4 py-2.5 font-bold">Detalhe</th>
                  <th className="text-right px-4 py-2.5 font-bold">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => {
                  const st = STATUS_LABEL[log.status] || { texto: log.status, cor: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-2.5 text-slate-700">{log.evento_codigo}</td>
                      <td className="px-4 py-2.5 text-slate-700">{log.os_id ? `#${log.os_id}` : '-'}</td>
                      <td className="px-4 py-2.5 text-slate-500">{log.email || '-'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-md text-3xs font-bold ${st.cor}`}>{st.texto}</span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-400 max-w-xs truncate" title={log.erro_detalhe || ''}>{log.erro_detalhe || '-'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {log.status === 'erro' && (
                          <button
                            type="button"
                            onClick={() => handleReenviar(log.id)}
                            disabled={reenviandoId === log.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-3xs font-bold text-slate-600 disabled:opacity-50 cursor-pointer"
                          >
                            {reenviandoId === log.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Reenviar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
